import SEO from '../../components/SEO';
import React, { useState } from 'react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import AppointmentModal from '../../components/AppointmentModal';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralsPage = () => {
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [insuranceProviders, setInsuranceProviders] = useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const insuranceRes = await axios.get(`${API}/insurance`);
        setInsuranceProviders(insuranceRes.data);
      } catch (error) {
        // Error handled silently
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <SEO title="Submit a Referral" description="Submit an OMHC or PRP referral to SDORA Wellbeing online. Providers and agencies can refer clients for outpatient therapy, psychiatric rehabilitation, and case management in Maryland." path="/services/referrals" />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Referrals
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 max-w-3xl">
            Connecting you to the right specialists and resources when you need services beyond our scope.
          </p>
        </div>
      </div>

      {/* Service Description */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What are Referrals?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              When a needed service is outside our organization's scope, we guide individuals to appropriate outside specialists, services, and community resources. Our goal is to ensure you receive adequate support tailored to your unique needs, even when it requires connecting you with partners beyond our clinic.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Benefits</h3>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <span className="text-purple-600 text-sm">✓</span>
                </span>
                <span className="text-gray-700">Access to specialized care beyond our clinic's offerings</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <span className="text-purple-600 text-sm">✓</span>
                </span>
                <span className="text-gray-700">Trusted recommendations and warm handoffs</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <span className="text-purple-600 text-sm">✓</span>
                </span>
                <span className="text-gray-700">Continuity of care coordination</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                  <span className="text-purple-600 text-sm">✓</span>
                </span>
                <span className="text-gray-700">Comprehensive support network tailored to your needs</span>
              </li>
            </ul>

            {/* Referral Flow */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How the Referral Process Works</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Referral or Client Inquiry</p>
                    <p className="text-gray-600 text-sm">Initial contact with our clinic</p>
                  </div>
                  <ArrowRight className="text-purple-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Comprehensive Assessment</p>
                    <p className="text-gray-600 text-sm">Understanding your needs</p>
                  </div>
                  <ArrowRight className="text-purple-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Eligibility Review</p>
                    <p className="text-gray-600 text-sm">Determining appropriate services</p>
                  </div>
                  <ArrowRight className="text-purple-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Individualized Treatment Plan</p>
                    <p className="text-gray-600 text-sm">Collaborative care planning</p>
                  </div>
                  <ArrowRight className="text-purple-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Recommended Services</p>
                    <p className="text-gray-600 text-sm">Internal or external resources</p>
                  </div>
                  <ArrowRight className="text-purple-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">6</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Ongoing Support</p>
                    <p className="text-gray-600 text-sm">Continuous care coordination</p>
                  </div>
                  <ArrowRight className="text-purple-400 flex-shrink-0" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold">7</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">External Referral if Needed</p>
                    <p className="text-gray-600 text-sm">Connection to specialized providers</p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowAppointmentModal(true)}
              className="w-full md:w-auto bg-purple-600 text-white px-8 py-4 rounded-lg hover:bg-purple-700 transition-colors font-semibold text-lg"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      <Footer />
      
      {showAppointmentModal && (
        <AppointmentModal 
          onClose={() => setShowAppointmentModal(false)}
          insuranceProviders={insuranceProviders}
        />
      )}
    </div>
  );
};

export default ReferralsPage;